import {AfterViewInit, Component, ElementRef, OnDestroy, TemplateRef, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BizConferenceApiService} from '../api/biz-conference-api.service';
import {Router} from '@angular/router';
import {PaymentApiService} from '../api/payment-api.service';
import {ToastrService} from 'ngx-toastr';
import {STATES} from '../constants';
import {EmailApiService} from '../api/email-api.service';
import {UserApiService} from '../api/user-api.service';
import {SpinnerService} from '../shared/services/spinner.service';
import {finalize, forkJoin} from 'rxjs';
import {Swiper} from "swiper";
import {SwiperOptions} from "swiper/types";
import GLightbox from 'glightbox';
import {map} from "rxjs/operators";
import {ConferenceFileService} from "../api/conference.file.service";
import {HttpErrorResponse} from "@angular/common/http";
import {Subject} from 'rxjs';
import {DataTableDirective} from 'angular-datatables';
import {escapeHtml, ranaDtOptions, RanaDtOptions} from '../shared/rana-datatable-defaults';

@Component({
    selector: 'app-biz-conference',
    templateUrl: './biz-conference.component.html',
    styleUrls: ['./biz-conference.component.css']
})
export class BizConferenceComponent implements AfterViewInit, OnDestroy {
    @ViewChild('swiperContainer', {static: true}) swiperContainer!: ElementRef<HTMLDivElement>;
    @ViewChild(DataTableDirective) dtElement!: DataTableDirective;
    @ViewChild('badgeTpl') badgeTpl!: TemplateRef<any>;
    @ViewChild('actionTpl') actionTpl!: TemplateRef<any>;
    private swiper: Swiper | null = null;
    bizForm!: FormGroup;
    registrationList: any[] = [];
    selectedSpeakerId!: string;

    dtOptions: RanaDtOptions = ranaDtOptions({columns: []});
    dtTrigger: Subject<any> = new Subject();

    canEdit: boolean = false;
    existingConferenceData: any;

    packageLabels: any = {
        adult: 'Adult',
        adult_single_room: 'Adult Single Room',
        adult_shared_room: 'Adult Shared Room',
        couple: 'Couple',
        local_full_food: 'Local - Full Event Food Only'
    };


    // Only packages that appear in dropdown
    packages = [
        {id: 'adult_single_room', label: 'Adult Single Room – $450 USD)', usd: 450},
        {id: 'adult_shared_room', label: 'Adult Shared Room– $325 USD)', usd: 325},
        {id: 'couple', label: 'Couple – $650 USD', usd: 650},
        {id: 'local_full_food', label: 'Local - Full Event Food Only – $300 USD', usd: 300},
    ];

    // Room / Kids options handled separately
    roomOptions = [
        {id: 'additional_room', label: 'Additional Room – $295 USD', usd: 295},
    ];

    kidsOptionUSD = 185; // $185 USD for kids checkbox

    states = STATES; // List of states for dropdown

    page: number = 1;
    baseAmount: number = 0;
    roomAmount: number = 0;
    donationAmount: number = 0;
    surcharge: number = 0;
    totalWithSurcharge: number = 0;
    kidsAmount: number = 0;
    subTotal: number = 0;
    donationOptions = [1000, 2000, 3000, 5000, 10000];
    captchaResponse: string | null = null;
    userid: any;
    user: any;
// PPT Upload
    pptForm!: FormGroup;
    pptList: ConferencePpt[] = [];
    selectedFile: File | null = null;


    constructor(
        private fb: FormBuilder,
        private api: BizConferenceApiService,
        private router: Router,
        private paymentApi: PaymentApiService,
        private toastr: ToastrService,
        private emailApi: EmailApiService,
        private userApiService: UserApiService,
        private spinner: SpinnerService,
        private conferenceFileService: ConferenceFileService
    ) {
    }

    ngOnInit(): void {
        this.bizForm = this.fb.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            phone: ['', Validators.required],
            packageType: ['', Validators.required],
            speaker: [false],
            topic: [''],
            paymentType: ['stripe', Validators.required],
            addressLine1: ['', [Validators.required]],
            addressLine2: [''],
            city: ['', [Validators.required]],
            state: ['', [Validators.required]], // Email field
            zipCode: ['', [Validators.required]],
            country: ['', [Validators.required]],
            captcha: new FormControl(null, Validators.required),

            additionalRoom: [false], // checkbox for additional room
            kidsOption: [false],     // new checkbox for kids
            donationAmount: [null],
            amount: [null]
        });

        this.pptForm = this.fb.group({
            title: ['', Validators.required],
            companyName: ['', Validators.required],
            description: ['', Validators.required],
            file: [null, Validators.required]
        });

        this.loadAllPpts();



        this.loadRegistrations();


        // Recalculate totals whenever payment type changes
        this.bizForm.get('paymentType')?.valueChanges.subscribe(() => {
            this.updateTotal();
        });

        // Optional: recalc totals when other options change
        this.bizForm.get('packageType')?.valueChanges.subscribe(() => this.updateTotal());
        this.bizForm.get('additionalRoom')?.valueChanges.subscribe(() => this.updateTotal());
        this.bizForm.get('kidsOption')?.valueChanges.subscribe(() => this.updateTotal());

        this.bizForm.get('donationAmount')?.valueChanges.subscribe(donationValue => {
            const donation = donationValue ? +donationValue : 0;
            this.donationAmount = donation;
            this.updateTotal();
        });


        this.bizForm.get('packageType')?.valueChanges.subscribe(selectedId => {
            const pkg = this.packages.find(p => p.id === selectedId);
            this.baseAmount = pkg ? pkg.usd : 0;
            this.updateTotal();
        });

        // Room option value lookup
        this.bizForm.get('additionalRoom')?.valueChanges.subscribe(checked => {
            this.roomAmount = checked ? 295 : 0; // $295 USD
            this.updateTotal();
        });


        // Kids checkbox
        this.bizForm.get('kidsOption')?.valueChanges.subscribe(checked => {
            this.kidsAmount = checked ? this.kidsOptionUSD : 0;
            this.updateTotal();
        });

        this.bizForm.disable();

    }

    onPptFileSelected(event: any) {
        const file = event.target.files[0];
        if (!file) return;

        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext !== 'ppt' && ext !== 'pptx') {
            this.toastr.error('Only PPT or PPTX files are allowed');
            return;
        }

        this.selectedFile = file;
    }



    uploadPpt() {
        if (!this.selectedFile) return;

        const isLocal = window.location.hostname === 'localhost'; // detect environment
        const file = this.selectedFile;

        this.conferenceFileService.uploadPresentation(this.user.id, file, isLocal)
            .subscribe({
                next: (event: any) => {
                    // If using Angular HttpClient with observe: 'events', you can track progress here
                    if (event.type) {
                        // Optionally handle upload progress
                        // console.log('Upload event', event);
                    } else {
                        // Upload complete
                        const filename = isLocal ? event.filename : file.name;

                        // Save PPT metadata
                        const pptMeta: Partial<ConferencePpt> = {
                            title: this.pptForm.value.title,
                            description: this.pptForm.value.description,
                            companyName:this.pptForm.value.companyName,
                            filename: filename,
                            uploadedBy: this.user.id
                        };

                        this.api.savePptMetadata(this.user.id, pptMeta)
                            .subscribe({
                                next: (saved) => {
                                    this.toastr.success('PPT metadata saved');
                                    this.pptList.unshift(saved); // Add to local list
                                    this.pptForm.reset();
                                    this.selectedFile = null;
                                    this.loadAllPpts();
                                },
                                error: (err) => this.toastr.error('Failed to save PPT metadata')
                            });
                    }
                },
                error: (err) => this.toastr.error('File upload failed')
            });
    }


    loadAllPpts() {
        this.api.getSpeakerPpts().subscribe({
            next: (res: ConferencePpt[]) => {
                if (!res || res.length === 0) {
                    this.pptList = [];
                    return;
                }

                // For each PPT, call user API to get firstname + lastname
                const requests = res.map(ppt =>
                    this.userApiService.getShortData(ppt.uploadedBy).pipe(
                        map(user => ({
                            ...ppt,
                            uploadedByName: user.firstname + ' ' + user.lastname,
                            email: user.email
                        }))
                    )
                );

                // Execute all requests in parallel
                forkJoin(requests).subscribe({
                    next: (enrichedPpts) => {
                        // Sort by createdDate
                        this.pptList = enrichedPpts.sort((a, b) =>
                            new Date(b.createdDate!).getTime() -
                            new Date(a.createdDate!).getTime()
                        );
                    },
                    error: (err) => {
                        console.error('Failed to enrich PPTs', err);
                        this.pptList = res; // fallback to raw data
                    }
                });
            },
          //  error: () => this.toastr.error('Failed to load presentations')
        });
    }


    selectSpeaker(speaker: Speaker) {
        this.selectedSpeakerId = speaker.id;
    }

    downloadPpt(ppt: ConferencePpt) {
        const speakerId = ppt.uploadedBy; // <-- use uploadedBy from backend
        if (!speakerId || !ppt.filename) {
            console.error('Invalid PPT data', ppt);
            return;
        }
        const isLocal = window.location.hostname === 'localhost'; // detect environment

        this.api.downloadSpeakerPresentation(speakerId, ppt.filename, isLocal)
            .subscribe({
                next: (resp: any) => {
                    if (isLocal) {
                        const blob = resp as Blob;
                        this.saveBlob(blob, ppt.filename);
                    } else {
                        this.forceDownload(resp.downloadUrl, ppt.filename);
                    }
                },
                error: (err: HttpErrorResponse) => {
                    console.error('Download failed', err);
                }
            });


    }


    private forceDownload(url: string, filename: string): void {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename; // browser hint
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    private saveBlob(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        this.forceDownload(url, filename);
        window.URL.revokeObjectURL(url);
    }




    loadUserConferenceData(email: string) {

        if (!this.registrationList || this.registrationList.length === 0) {
            console.warn("Registration list not loaded yet!");
            return;
        }

        const conf = this.registrationList.find(r => r.email === email);

        if (conf) {
            this.existingConferenceData = conf;

            // Pre-fill form
            this.bizForm.patchValue(conf);

            // Permission check
            this.canEdit = conf.email === this.user.email;

            if (!this.canEdit) {
                this.bizForm.disable();
                this.bizForm.get('email')?.disable();
                this.bizForm.get('firstName')?.disable();
                this.bizForm.get('lastName')?.disable();
                this.bizForm.get('phone')?.disable();
                this.bizForm.get('addressLine1')?.disable();
                this.bizForm.get('addressLine2')?.disable();
                this.bizForm.get('city')?.disable();
                this.bizForm.get('zipCode')?.disable();
                this.bizForm.get('country')?.disable();
                this.bizForm.get('paymentType')?.disable();

            } else {
                this.bizForm.enable();

            }

        } else {
            // No registration found → allow user to register new
            this.existingConferenceData = null;
            this.canEdit = true;
            this.bizForm.enable();

            this.bizForm.patchValue({
                email: this.user.email,
                firstName: this.user.firstname,
                lastName: this.user.lastname,
                phone: this.user.phone,
                addressLine1: this.user.addressLine1,
                addressLine2: this.user.addressLine2,
                city: this.user.city,
                state: this.user.state,
                zipCode: this.user.zipCode,
                country: this.user.country
            });


        }
    }


    updateTotal() {
        // Base subtotal from package, room, and kids options
        this.subTotal = this.baseAmount + this.roomAmount + this.kidsAmount;

        //const donation = this.bizForm.get('donationAmount')?.value || 0;
        //const grossTotal = this.subTotal + donation;

        // Special rule: if donation amount  >= 1000 and (donation amoount - package price) >200, waive package amount
        if (this.donationAmount >= 1000 && (this.donationAmount - this.subTotal) > 200) {
            this.subTotal = 0;
        }

        const isStripe = this.bizForm.get('paymentType')?.value === 'stripe';

        if (isStripe) {
            // Calculate Stripe surcharge on subtotal + donation
            const totalBeforeSurcharge = this.subTotal + this.donationAmount;
            const percentage = 0.029;
            const fixed = 0.30;
            const grossCharge = (totalBeforeSurcharge + fixed) / (1 - percentage);
            this.surcharge = parseFloat((grossCharge - totalBeforeSurcharge).toFixed(2));
            this.totalWithSurcharge = parseFloat((totalBeforeSurcharge + this.surcharge).toFixed(2));
        } else {
            // No surcharge for Zelle or Check
            this.surcharge = 0;
            this.totalWithSurcharge = this.subTotal + this.donationAmount;
        }
    }



    loadRegistrations() {
        this.api.getAllEntries().subscribe({
            next: (res: any[]) => {
                this.registrationList = res.sort((a: any, b: any) =>
                    new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
                );
                this.refreshTableData();

                this.userid = sessionStorage.getItem('userId');

                if (this.userid) {
                    this.spinner.show();
                    this.userApiService.getData(this.userid)
                        .pipe(finalize(() => this.spinner.hide()))
                        .subscribe((data) => {
                            this.user = data;             // logged in user

                            this.selectedSpeakerId = this.userid
                            // Now load their conference registration
                            this.loadUserConferenceData(this.user.email);
                        });
                }


            },
            //error: (err) => this.toastr.error('Failed to load registrations', 'Error')
        });
    }

    submitForm() {
        if (this.bizForm.invalid) return;

        this.bizForm.patchValue({
            donationAmount: this.donationAmount,
            amount: this.totalWithSurcharge
        });
        const formData = this.bizForm.getRawValue();

        // 👉 If editing, call update instead of create
        if (this.existingConferenceData) {
            const updatePayload = {
                ...formData,
                id: this.existingConferenceData.id
            };

            this.updateRegistration(updatePayload);
            return;
        }

        if (formData.paymentType !== 'stripe') {
            this.api.createConferenceEntry({
                ...formData,
                captcha: this.captchaResponse
            }).subscribe({
                next: (res: any) => {
                    // Add new registration to the top of the list
                    this.registrationList.unshift({...formData, createdDate: new Date()});
                    this.refreshTableData();

                    // Open Registered Attendees tab
                    const attendeeCollapse = document.getElementById('collapseBizList');
                    const formCollapse = document.getElementById('collapseBizForm');
                    const detailsCollapse = document.getElementById('collapseBizDetails');

                    if (attendeeCollapse && formCollapse) {
                        formCollapse.classList.remove('show');
                        attendeeCollapse.classList.add('show');
                        detailsCollapse?.classList.remove('show');

                        // Update left menu button states
                        document.querySelector('[data-target="#collapseBizForm"]')?.setAttribute('aria-expanded', 'false');
                        document.querySelector('[data-target="#collapseBizList"]')?.setAttribute('aria-expanded', 'true');
                        document.querySelector('[data-target="#collapseBizDetails"]')?.setAttribute('aria-expanded', 'false');

                    }

                    // 📧 Send email
                    this.emailApi.sendRegistrationEmail({
                        name: formData.firstName + ' ' + formData.lastName,
                        email: formData.email,
                        phone: formData.phone,
                        subject: 'Business Conference 2026 Registration',
                        message:
                            'New Business Conference Registration\n' +
                            'Transaction ID: ' + res.id + '\n' +
                            'Amount: ' + formData.amount + ' ' + 'USD'
                    }).subscribe();


                    // Show success message
                    this.toastr.success('Registration saved. Please complete payment via Zelle or Check.', 'Success');

                    // Reset form
                    this.bizForm.reset({paymentType: 'stripe', country: 'USA'});
                },
                error: (err) => this.toastr.error(err.error || 'Registration failed', 'Error')
            });
            return;
        }

        // Stripe flow: save the registration first (the server stores it as PENDING_PAYMENT) so the card payment can
        // always be traced to it, even if the payer never returns from Stripe. The server marks it paid.
        this.spinner.show();
        this.api.createConferenceEntry({
            ...formData,
            captcha: this.captchaResponse
        }).pipe(finalize(() => this.spinner.hide())).subscribe({
            next: (entry: any) => {
                const stripeRequest = {
                    package: formData.packageType + (formData.additionalRoom ? '_with_additional_room' : '') + (formData.kidsOption ? '_with_kids' : ''),
                    country: formData.country,
                    subTotal: Math.round((this.subTotal + this.donationAmount) * 100),
                    businessConferenceId: entry.id
                };
                sessionStorage.setItem('pendingBizConference', JSON.stringify({
                    ...formData,
                    businessConferenceId: entry.id
                }));

                this.paymentApi.createBizCheckoutSession(stripeRequest).subscribe({
                    next: (res) => {
                        const stripe = (window as any).Stripe(res.publicKey);
                        stripe.redirectToCheckout({sessionId: res.sessionId});
                    },
                    error: (err) => this.toastr.error(err.error?.error || err.error || 'Stripe checkout failed', 'Error')
                });
            },
            error: (err) => this.toastr.error(err.error || 'Registration failed', 'Error')
        });
    }


    editRegistration(reg: any) {
        this.existingConferenceData = reg;
        this.canEdit = true;

        this.bizForm.enable();
        this.bizForm.get('email')?.disable(); // keep email readonly
        this.bizForm.get('firstName')?.disable();
        this.bizForm.get('lastName')?.disable();
        this.bizForm.get('phone')?.disable();
        this.bizForm.get('addressLine1')?.disable();
        this.bizForm.get('addressLine2')?.disable();
        this.bizForm.get('city')?.disable();
        this.bizForm.get('state')?.disable();
        this.bizForm.get('zipCode')?.disable();
        this.bizForm.get('country')?.disable();
        this.bizForm.get('paymentType')?.disable();

        this.bizForm.patchValue({
            firstName: reg.firstName,
            lastName: reg.lastName,
            email: reg.email,
            phone: reg.phone,
            packageType: reg.packageType,
            speaker: reg.speaker,
            topic: reg.topic,
            paymentType: reg.paymentType,
            addressLine1: reg.addressLine1,
            addressLine2: reg.addressLine2,
            city: reg.city,
            state: reg.state,
            zipCode: reg.zipCode,
            country: reg.country,
            additionalRoom: reg.additionalRoom,
            kidsOption: reg.kidsOption,
            donationAmount: reg.donationAmount,
            captcha: null              // IMPORTANT

        });

        // Now show the form panel
        const formCollapse = document.getElementById('collapseBizForm');
        const attendeeCollapse = document.getElementById('collapseBizList');

        attendeeCollapse?.classList.remove('show');
        formCollapse?.classList.add('show');
    }


    calculateStripeTotal(amount: number): number {
        const percentage = 0.029;
        const fixed = 0.30;
        const grossCharge = (amount + fixed) / (1 - percentage);
        return parseFloat(grossCharge.toFixed(2));
    }

    onCaptchaResolved(token: string | null = null) {
        this.bizForm.patchValue({captcha: token});
        this.captchaResponse = token;
    }


    detailsOpen = true;

    toggleDetails() {
        this.detailsOpen = true;
    }

    toggleOthers() {
        this.detailsOpen = false;
    }


    updateRegistration(payload: any) {
        this.spinner.show();

        this.api.updateConferenceEntry(payload.id, payload)
            .pipe(finalize(() => this.spinner.hide()))
            .subscribe({
                next: () => {
                    this.toastr.success('Registration updated successfully');

                    this.existingConferenceData = null;  // reset edit mode
                    this.loadRegistrations();

                    // Return to attendee list
                    const attendeeCollapse = document.getElementById('collapseBizList');
                    const formCollapse = document.getElementById('collapseBizForm');

                    formCollapse?.classList.remove('show');
                    attendeeCollapse?.classList.add('show');
                },
                error: () => this.toastr.error('Update failed')
            });
    }


    private lightbox: any;
    ngAfterViewInit(): void {
        if (this.swiperContainer && this.swiperContainer.nativeElement) {
            this.swiper = new Swiper(this.swiperContainer.nativeElement, this.getSwiperOptions());
        }
        this.lightbox = GLightbox({
            selector: '.glightbox', // This matches the class in your HTML
            touchNavigation: true,
            loop: true,
            autoplayVideos: true
        });

        this.buildDtOptions();
    }

    // Action column only exists for logged-in users (matches the original *ngIf="user?.email"
    // on the <th>/<td>) — column sets can't change after the table is first triggered, so this
    // is decided once, up front, using the synchronously-known `userid` rather than waiting on
    // the async `user` fetch inside loadRegistrations().
    private buildDtOptions(): void {
        const showActionColumn = !!sessionStorage.getItem('userId');
        const columns: RanaDtOptions['columns'] = [
            {title: '#', data: null, orderable: false, render: (data: any, type: any, row: any, meta: any) => meta.row + 1},
            {
                title: 'Full Name', data: null,
                render: (data: any, type: any, row: any) => `${escapeHtml(row.firstName)} ${escapeHtml(row.lastName)}`
            },
            {title: 'Donation Amount', data: 'donationAmount', render: (d: any) => (d > 500 ? escapeHtml(d) : '')},
            {title: 'Badge', data: null, orderable: false, ngTemplateRef: {ref: this.badgeTpl, context: {captureEvents: () => {}}}},
            {title: 'Payment Type', data: 'paymentType', render: (d: any) => escapeHtml(d)}
        ];
        if (showActionColumn) {
            columns.push({title: 'Action', data: null, orderable: false, ngTemplateRef: {ref: this.actionTpl, context: {captureEvents: () => {}}}});
        }

        this.dtOptions = ranaDtOptions({columns});
    }

    private refreshTableData(): void {
        this.dtOptions.data = this.registrationList;
        this.rerenderTable();
    }

    private rerenderTable(): void {
        if (this.dtElement?.dtInstance) {
            this.dtElement.dtInstance.then(dt => {
                dt.destroy();
                this.dtTrigger.next(null);
            });
        } else {
            this.dtTrigger.next(null);
        }
    }

    openVideo() {
        this.lightbox.open();
    }

    ngOnDestroy(): void {
        if (this.swiper) {
            this.swiper.destroy();
        }
        this.dtTrigger.unsubscribe();
    }

    private getSwiperOptions(): SwiperOptions {
        return {
            // Global defaults
            loop: true,
            spaceBetween: 30, // Added a little gap so speaker cards don't touch
            speed: 2000,       // Faster transition for a "snappy" feel

            autoplay: {
                delay: 2000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true, // Good UX: stops moving when user hovers to click "View Profile"
            },

            // Responsive behavior
            breakpoints: {
                0: {
                    slidesPerView: 1,
                    spaceBetween: 10,
                },
                768: {
                    slidesPerView: 2, // 2 slides usually looks better on tablets
                    spaceBetween: 20,
                },
                992: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                }
            },

            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true,
            },
        };
    }


    openVideo1(videoUrl: string) {
        // your existing modal / fancybox / glightbox logic
        console.log(videoUrl);
    }
speakers: Speaker[] = [
    {
        id:'1',
        name: 'Karn Singh',
        role: 'Keynote Speaker',
        company: 'Workforce transformation Professional',
        topic: 'ConvoEdge, AI-driven conversational platform & Yunikee - Sign language for Deaf',
        image: 'assets/img/biz-conf/Karn.jpeg',
        tags: ['AI', 'Social Enterprise', ' Talent Transformation'],
        video: '/assets/vid/conf-speaker/Karn.mp4'
    },
    {
        id:'2',
        name: 'Ritambhara Chauhan',
        role: 'Featured Speaker',
        company: 'Managing Partner, My Startup Lawyer, PLLC',
        topic: 'Beyond the Basics: Legal Nuances for Startups & Small Businesses',
        image: 'assets/img/biz-conf/Ritambhara.png',
        tags: ['Startup Law', 'Entrepreneurship', 'Global Business', 'Innovation']
    },
    {
        id:'3',
        name: 'Dr. Sanket Chauhan',
        role: 'Featured Speaker',
        company: 'Founder & CEO, Surgical Automations, Inc.',
        topic: 'From Scalpel to Robot: How a Visionary Surgeon Is Building the Tesla of Surgical Robotics with AI and Automation',
        image: 'assets/img/biz-conf/Sanket.png',
        tags: ['Surgical Robotics', 'AI in Healthcare', 'Digital Health', 'Medical Innovation']

    },
    {
        id:'4',
        name: 'Vineet Tomer',
        role: 'Featured Speaker',
        company: 'Financial Services Leader & Entrepreneur',
        topic: 'Wealth Strategy, Financial Empowerment & Entrepreneurial Leadership',
        image: 'assets/img/biz-conf/Vineet.jpeg',
        tags: ['Financial Planning', 'Entrepreneurship', 'Wealth Management', 'Leadership'],
        video: '/assets/vid/conf-speaker/PadeepNVineet.mp4'
    },
    {
        id:'5',
        name: 'Pradeep Sengar',
        role: 'Featured Speaker',
        company: 'Financial Services Consultant & Wealth Strategist',
        topic: 'Tax-Efficient Wealth Building, Financial Literacy & Generational Legacy',
        image: 'assets/img/biz-conf/Pradeep.jpg',
        tags: ['Wealth Strategy', 'Financial Literacy', 'Tax Planning', 'Generational Wealth'],
        video: '/assets/vid/conf-speaker/PadeepNVineet.mp4'
    },
    {
        id:'6',
        name: 'Dhiraj Singh',
        role: 'Keynote Speaker',
        company: 'Founder & CEO, Tensor Analytic',
        topic: ' Data analytics and AI company focused on building modern decision systems for businesses',
        image: 'assets/img/biz-conf/Diraj.png?auto=format&fit=crop&w=600&q=80',
        tags: ['AI/ML', 'Data Analytics', 'Forcasting'],
        video: '/assets/vid/conf-speaker/Dhiraj.mp4'
    },
    {
        id:'7',
        name: 'Dr.Partap Chauhan',
        role: 'Keynote Speaker',
        company: 'Ayurvedic physician, author, public speaker & Founder director of Jiva Ayurveda',
        topic: 'Prevention in Auyurveda',
        image: 'assets/img/biz-conf/Pratap.png',
        tags: ['Ayurveda', 'Medicine', 'Peventative'],
        video: '/assets/vid/conf-speaker/Pratap.mp4'
    },
    {
        id:'1',
        name: 'Dr. Neelam Chudasama',
        role: 'Keynote Speaker',
        company: 'Founder and Director of Life Ayurveda Wellness Center and owns  lifeayurvedausa.com',
        topic: ' Ayurveda as a business',
        image: 'https://ui-avatars.com/api/?name=NC&size=600&background=ff7300&color=fff',
        tags: ['Life Ayurveda', 'Medicine', 'Yoga'],
        video: '/assets/vid/conf-speaker/Neelam.mp4'
    }, {
        id:'1',
        name: 'Rohit Chauhan',
        role: 'Keynote Speaker',
        company: 'Founder, AeroTwin',
        topic: 'Digital Twins for Propulsion, Physics-ML Hybrid Approaches, and AI-Accelerated Design Iteration',
        image: 'assets/img/biz-conf/Rohit.png',
        tags: ['Digital-twin architecture', 'Aero', 'AI/ML'],
        video: '/assets/vid/conf-speaker/Rohit.mp4'
    }, {
        id:'1',
        name: 'Dr. Rajendra Singh Rajput',
        role: 'Keynote Speaker',
        company: 'Plastic & Hair Transplant Surgeon | Hair Restoration Innovator',
        topic: '',
        image: 'assets/img/biz-conf/Rajendra.png',
        tags: ['Innovator', 'Surgeon', 'Plastic & Hair Transplant'],
        video: '/assets/vid/conf-speaker/Rajendra.mp4'
    },
    {
        id:'1',
        name: "Daleep Shekhawat",
        role: "Financial Advisor & Community Leader",
        company: "President, RANA Canada West Chapter | Director, CHCC & Nimittekam Canada",
        topic: "New Art of Living",
        image: "assets/img/biz-conf/Daleep.png?auto=format&fit=crop&w=600&q=80",
        tags: [ "Wealth Management",  "Community Leadership"],
        video: "/assets/vid/conf-speaker/Daleep.mp4"
    },
    {
        id:'1',
        name: 'Manish Singh & Meenal Singh',
        role: 'Entrepreneurs',
        company: 'Multi-Brand Franchise & Investment Leaders',
        topic: 'Building Scalable, Diversified Businesses',
        image: 'assets/img/biz-conf/Manish.png',
        tags: [
            'Franchising',
            'Business Growth',
            'Investments'
        ],
        video: '/assets/vid/conf-speaker/Manish.mp4'
    }


];




}



interface Speaker {
    id: string;
    name: string;
    role: string;
    company: string;
    topic: string;
    image: string;
    tags: string[];
    video?: string;
}
export interface ConferencePpt {
    id?: string;
    speakerId: string;   // ✅ REQUIRED
    title: string;
    companyName: string;
    description: string;
    filename: string;
    uploadedBy: string;
    uploadedByName: string;
    email: string;
    createdDate?: Date;
}
